import { useRelativeTime } from "@/composables/useRelativeTime";
import { useUserStore } from "@/store";
import type { Account } from "@aircap/common/account";
import { getAccountState, getProviderType, isOpeningFormSent } from "@aircap/common/account";
import { getSwanKybStage } from "@aircap/common/swan";
import type { Timestamp } from "@firebase/firestore-types";
import type { Ref } from "vue";
import { computed } from "vue";
import { useI18n } from "./useI18n";

export const useAccountState = (account: Ref<Account | undefined>) => {
  const user = useUserStore();
  const { t } = useI18n();
  const { getRelativeTime } = useRelativeTime();

  const accountState = computed(() => (account.value ? getAccountState(account.value) : undefined));

  const sinceDays = (since: Timestamp | null) =>
    since
      ? t("swanKyb.since", { count: Math.floor((Date.now() - since.toMillis()) / 86_400_000) })
      : "";

  const accountStateUi = computed(() => {
    if (!accountState.value)
      return { icon: "mdi-alert", color: "error", message: "Account not found" };

    switch (accountState.value.status) {
      case "synced":
        return {
          icon: "mdi-check",
          color: "success",
          message: t("account.status.synced"),
        };
      case "refused":
        return {
          icon: "mdi-close-octagon",
          color: "error",
          message: t("account.status.refused"),
        };
      case "kycReadyForAdminReview":
        return {
          icon: "mdi-timer-sand",
          color: "#9E9E9E",
          message: t("account.status.opening"),
          adminDetail: user.isKind("admin") ? t("account.status.awaitingAircapReview") : undefined,
        };
      case "pending": {
        if (account.value?.accountType === "treezor") {
          if (!isOpeningFormSent(account.value))
            return {
              icon: "mdi-timer-sand",
              color: "#9E9E9E",
              message: user.isKind("admin")
                ? t("account.status.pendingOpening")
                : t("account.status.awaitingHolderSubmission"),
            };
          return {
            icon: "mdi-timer-sand",
            color: "#9E9E9E",
            message: t("account.status.opening"),
            adminDetail: user.isKind("admin") ? t("account.status.sentToTreezor") : undefined,
          };
        }

        if (
          !user.isKind("admin") &&
          account.value &&
          getProviderType(account.value.accountType) === "proAccount"
        )
          return {
            icon: "mdi-timer-sand",
            color: "#9E9E9E",
            message: isOpeningFormSent(account.value)
              ? t("account.status.awaitingReview")
              : t("account.status.awaitingHolderSubmission"),
          };

        const swanKybStage = account.value && getSwanKybStage(account.value);
        if (swanKybStage) {
          let message = "";
          switch (swanKybStage.stage) {
            case "formInProgress":
            case "formBlocked":
            case "verificationNotStarted":
            case "refused":
            case "accountWithoutIban":
              message = t(`swanKyb.stage.${swanKybStage.stage}`);
              break;
            case "waitingForDocuments":
              message =
                swanKybStage.count === 1
                  ? t("swanKyb.stage.waitingForDocument", {
                      document: t(`swanKyb.documents.${swanKybStage.purpose}`),
                      since: sinceDays(swanKybStage.since),
                    })
                  : t("swanKyb.stage.waitingForDocuments", {
                      count: swanKybStage.count,
                      since: sinceDays(swanKybStage.since),
                    });
              break;
            case "waitingForInformation":
              message = t("swanKyb.stage.waitingForInformation", {
                requirement: t(`swanKyb.requirements.${swanKybStage.requirement}`),
                since: sinceDays(swanKybStage.since),
              });
              break;
            case "inReview":
              message = t("swanKyb.stage.inReview", { since: sinceDays(swanKybStage.since) });
              break;
          }
          return {
            icon: swanKybStage.stage === "refused" ? "mdi-close-octagon" : "mdi-timer-sand",
            color: swanKybStage.stage === "refused" ? "error" : "#9E9E9E",
            message: message.trim(),
          };
        }

        return {
          icon: "mdi-timer-sand",
          color: "#9E9E9E",
          message:
            account.value && getProviderType(account.value.accountType) === "proAccount"
              ? t("account.status.pendingOpening")
              : t("account.status.pendingConnection") +
                (user.isKind("admin") && accountState.value.infoForAdmin
                  ? `(${accountState.value.infoForAdmin})`
                  : ""),
        };
      }
      case "error":
        if (user.isKind("admin")) {
          return {
            icon: "mdi-alert",
            color: "error",
            message: [
              accountState.value.infoForAdmin,
              account.value?.lastStatus?.status === "error" && account.value.lastStatus.updatedAt
                ? `In error ${getRelativeTime(account.value.lastStatus.updatedAt.toDate())}`
                : null,
            ]
              .filter(Boolean)
              .join(" - "),
          };
        } else {
          return { icon: "mdi-check", color: "success" };
        }
      case "reconnectionNeeded":
        return {
          icon: "mdi-account-reactivate",
          color:
            account.value?.lastStatus &&
            Date.now() >= account.value.lastStatus.updatedAt.toMillis() + 14 * 24 * 60 * 60 * 1000
              ? "error"
              : "#000000",
          message: `${t("account.status.expired")} ${
            user.isKind("admin")
              ? `(ADMIN: ${[
                  account.value?.lastStatus &&
                    `Expired ${getRelativeTime(account.value.lastStatus.updatedAt.toDate())}`,
                  accountState.value.infoForAdmin,
                ]
                  .filter(Boolean)
                  .join(" - ")})`
              : ""
          }`,
        };
      case "missingBankAccountId":
        if (user.isKind("admin")) {
          return { icon: "mdi-alert", color: "error", message: "Missing account ID" };
        } else {
          return {
            icon: "mdi-timer-sand",
            color: "#9E9E9E",
            message: t("account.status.missingBankAccountId"),
          };
        }
    }
  });

  return {
    accountStateUi,
    accountState,
  };
};
